import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { Search, Flame } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Festivals() {
  const [items, setItems] = useState([]);
  const [deities, setDeities] = useState({});
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const { hash } = useLocation();
  useSeo("Festival calendar of India");

  useEffect(() => {
    api.get("/festivals").then((r) => setItems(r.data.items));
    api.get("/deities").then((r) => setDeities(Object.fromEntries(r.data.items.map((d) => [d.slug, d]))));
  }, []);
  useEffect(() => {
    if (hash && items.length) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [hash, items]);

  const months = useMemo(() => {
    const f = items.filter((x) => (!q || `${x.name} ${x.region}`.toLowerCase().includes(q.toLowerCase()))
      && (scope === "all" || (scope === "pan" ? x.region === "Pan-India" : x.region !== "Pan-India")));
    const map = new Map();
    f.forEach((x) => { const d = new Date(x.date); const k = `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`; map.set(k, [...(map.get(k) || []), x]); });
    return [...map.entries()];
  }, [items, q, scope]);

  const next = items[0];

  return (
    <div data-testid="festivals-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">॥ उत्सव पंचांग ॥</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Festivals of India</h1>
      <p className="text-[#63534E] mt-5 text-base md:text-lg max-w-2xl">{items.length} pan-Indian and regional festivals, each linked to its deity. Dates follow the lunar calendar, so they move a little each year. Dates marked "est." are estimates.</p>

      {next && (
        <div data-testid="festival-next" className="mt-10 bg-[#4A0E17] text-[#FAF6F0] rounded-3xl p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="overline text-[#D4AF37]">Next festival</div>
            <div className="font-serif text-3xl md:text-4xl mt-2">{next.name}</div>
            <div className="text-[#FAF6F0]/75 mt-2">{next.significance}</div>
          </div>
          <div className="text-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-2xl px-8 py-5">
            <div className="font-mono text-5xl text-[#F3E5AB]">{next.days_away}</div>
            <div className="overline text-[#D4AF37]">days to go</div>
          </div>
        </div>
      )}

      <div className="mt-10 flex flex-col md:flex-row gap-4 md:items-center">
        <div className="relative md:w-80">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8C6D3B]" />
          <input data-testid="festival-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search festival or region (e.g. Kerala)…"
            className="w-full h-11 pl-10 pr-4 rounded-full bg-[#FFFDF9] border border-[#D4AF37]/40 text-sm focus:outline-none focus:border-[#4A0E17]" />
        </div>
        <div className="flex gap-2">
          {[["all", "All"], ["pan", "Pan-India"], ["regional", "Regional"]].map(([k, l]) => (
            <button key={k} data-testid={`festival-scope-${k}`} onClick={() => setScope(k)}
              className={`px-4 h-9 rounded-full text-sm border transition-sacred ${scope === k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40"}`}>{l}</button>
          ))}
        </div>
      </div>

      {months.map(([m, list]) => (
        <section key={m} className="mt-12">
          <h2 className="font-serif text-2xl md:text-3xl text-[#4A0E17] border-b border-[#D4AF37]/40 pb-3">{m}</h2>
          <div className="mt-5 space-y-3">{list.map((f) => <FestivalRow key={f.slug} f={f} deities={deities} active={hash === `#${f.slug}`} />)}</div>
        </section>
      ))}
    </div>
  );
}

function FestivalRow({ f, deities, active }) {
  const d = new Date(f.date);
  return (
    <div id={f.slug} data-testid={`festival-${f.slug}`} className={`bg-[#FFFDF9] sacred-border rounded-2xl p-5 flex gap-5 transition-sacred ${active ? "ring-2 ring-[#E65100]" : ""}`}>
      <div className="w-16 shrink-0 text-center">
        <div className="font-mono text-3xl text-[#4A0E17] leading-none">{d.getUTCDate()}</div>
        <div className="overline mt-1">{MONTHS[d.getUTCMonth()].slice(0, 3)}</div>
        {f.estimated && <div className="text-[10px] text-[#8C6D3B] mt-1">est.</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-serif text-xl text-[#4A0E17]">{f.name}</h3>
          <span className="font-devanagari text-sm text-[#8C6D3B]">{f.hindi}</span>
          <span className="text-xs bg-[#F3E5AB]/60 border border-[#D4AF37]/30 rounded-full px-2 py-0.5">{f.region}</span>
        </div>
        <p className="text-sm text-[#1F1412] mt-2">{f.significance}</p>
        {f.rituals && <p className="text-xs text-[#63534E] mt-1 flex items-center gap-1"><Flame className="w-3 h-3 text-[#E65100]" /> {f.rituals}</p>}
        {f.deities?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {f.deities.filter((s) => deities[s]).map((s) => (
              <Link key={s} to={`/deities/${s}`} data-testid={`festival-${f.slug}-deity-${s}`} className="flex items-center gap-1.5 text-xs text-[#4A0E17] hover:text-[#E65100] bg-white border border-[#D4AF37]/40 rounded-full pl-0.5 pr-2.5 py-0.5">
                <img src={deities[s].image_url} alt="" className="w-5 h-5 rounded-full object-cover" /> {deities[s].name}
              </Link>
            ))}
          </div>
        )}
      </div>
      <div className="hidden sm:block text-right font-mono text-xs text-[#8C6D3B] whitespace-nowrap">in {f.days_away}d</div>
    </div>
  );
}
